import {
  setFullStoryAnalyticsUserVars,
} from './fullstory.utils';

let analyticsEmailId: string | undefined;
let analyticsOktaId: string | undefined;
let analyticsUserUUID: string | undefined;

function normalizeEmailId(email: string | null | undefined): string | undefined {
  const normalized = email?.trim().toLowerCase();
  return normalized || undefined;
}

function normalizeOktaId(id: string | null | undefined): string | undefined {
  const normalized = id?.trim();
  return normalized || undefined;
}

function normalizeUserUUID(id: string | null | undefined): string | undefined {
  const normalized = id?.trim();
  return normalized || undefined;
}

function syncFullStoryUserVars(): void {
  setFullStoryAnalyticsUserVars({
    userID: analyticsEmailId,
    uuid: analyticsOktaId,
    userUUID: analyticsUserUUID,
  });
}

/**
 * Stores the current user's email for analytics (FullStory + Sumo/Kinesis).
 */
export function setAnalyticsEmailId(email: string | null | undefined): void {
  analyticsEmailId = normalizeEmailId(email);
  syncFullStoryUserVars();
}

/** Returns the normalized email currently associated with analytics events. */
export function getAnalyticsEmailId(): string | undefined {
  return analyticsEmailId;
}

/**
 * Stores the Okta id (_embedded.user.id) for analytics events.
 */
export function setAnalyticsOktaId(id: string | null | undefined): void {
  analyticsOktaId = normalizeOktaId(id);
  syncFullStoryUserVars();
}

/** Returns the Okta id currently associated with analytics events. */
export function getAnalyticsOktaId(): string | undefined {
  return analyticsOktaId;
}

/** Clears stored analytics email context. */
export function clearAnalyticsEmailId(): void {
  analyticsEmailId = undefined;
  syncFullStoryUserVars();
}

/** Clears stored Okta id analytics context. */
export function clearAnalyticsOktaId(): void {
  analyticsOktaId = undefined;
  syncFullStoryUserVars();
}

/**
 * Stores the FMC user UUID for analytics events (registration flow).
 */
export function setAnalyticsUserUUID(id: string | null | undefined): void {
  analyticsUserUUID = normalizeUserUUID(id);
  syncFullStoryUserVars();
}

/** Returns the user UUID currently associated with analytics events. */
export function getAnalyticsUserUUID(): string | undefined {
  return analyticsUserUUID;
}

/** Clears stored user UUID analytics context. */
export function clearAnalyticsUserUUID(): void {
  analyticsUserUUID = undefined;
  syncFullStoryUserVars();
}

/** Resets analytics user context (tests only). */
export function resetAnalyticsUserContextForTesting(): void {
  analyticsEmailId = undefined;
  analyticsOktaId = undefined;
  analyticsUserUUID = undefined;
}

/** @deprecated Use resetAnalyticsUserContextForTesting */
export function resetAnalyticsEmailIdForTesting(): void {
  resetAnalyticsUserContextForTesting();
}

/** Reads Okta id from session storage when available. */
export function syncAnalyticsOktaIdFromSession(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }

  try {
    const raw = sessionStorage.getItem('okta_user_info');
    if (!raw) {
      return;
    }

    const parsed = JSON.parse(raw) as { sub?: string };
    if (parsed.sub) {
      setAnalyticsOktaId(parsed.sub);
    }
  } catch {
    // Session storage is best-effort for analytics enrichment only.
  }
}

export function withAnalyticsUserContext<T extends Record<string, unknown>>(
  payload: T,
): T & { userID?: string; uuid?: string; userUUID?: string } {
  let enriched: T & { userID?: string; uuid?: string; userUUID?: string } = { ...payload };

  const userID = getAnalyticsEmailId();
  if (userID && enriched.userID == null) {
    enriched = { ...enriched, userID };
  }

  const uuid = getAnalyticsOktaId();
  if (uuid && enriched.uuid == null) {
    enriched = { ...enriched, uuid };
  }

  const userUUID = getAnalyticsUserUUID();
  if (userUUID && enriched.userUUID == null) {
    enriched = { ...enriched, userUUID };
  }

  return enriched;
}

/** @deprecated Use withAnalyticsUserContext */
export function withAnalyticsEmailId<T extends Record<string, unknown>>(
  payload: T,
): T & { userID?: string } {
  return withAnalyticsUserContext(payload);
}
